import * as React from 'react';
import { useMemo, useState } from 'react';
import { Form } from '@amzn/awsui-community-rjsf';
import validator from 'src/components/datausageagreement/cfWorkerFormValidator';
import { FieldProps, RegistryWidgetsType, WidgetProps } from '@rjsf/utils';
import { OmniDUAContext } from 'src/components/datausageagreement/widgets/OmniDUAContext';
import { DiffWidget, UNIT_SEPARATOR } from 'src/components/datausageagreement/widgets/DiffWidget';
import { CompactFieldTemplate, HIDDEN } from 'src/components/datausageagreement/templates/CompactFieldTemplate';
import { ReadOnlyArrayFieldTemplate } from 'src/components/datausageagreement/templates/ReadOnlyArrayFieldTemplate';
import { filterSchemaByContexts, sanitizeFormData, useRjsfSchema } from 'src/components/datausageagreement/utils';
import { flattenTreeToText } from 'src/components/datausageagreement/widgets/HierarchicalCheckboxField';

export interface UsageAgreementDiffProps {
  afterFormData: object;
  beforeFormData: object;
  errors: any[];
  setErrors: any;
  schema: string;
  showChangesOnly?: boolean;
  omniContext?: OmniDUAContext;
  contexts?: string[];
}

export const UsageAgreementDiff = (props: UsageAgreementDiffProps) => {
  const [propertyOrder, setPropertyOrder] = useState<string[]>([]);
  const [uiSchemaFromJson, setUiSchemaFromJson] = useState<any>(null);
  const rjsfSchema = useRjsfSchema(props.schema, setPropertyOrder, setUiSchemaFromJson);
  const filteredSchema = useMemo(
    () => filterSchemaByContexts(rjsfSchema, props.contexts || ['default']),
    [rjsfSchema, props.contexts],
  );
  const sanitizedFormDataBefore = useMemo(() => sanitizeFormData(props.beforeFormData), [props.beforeFormData]);
  const sanitizedFormDataAfter = useMemo(() => sanitizeFormData(props.afterFormData), [props.afterFormData]);
  // Don't filter the order array - let RJSF handle conditional field visibility
  // Conditional fields from allOf need to be in ui:order to appear in the correct position
  const filteredOrder = useMemo(() => {
    return propertyOrder;
  }, [propertyOrder]);

  // Keys from both snapshots tell us which conditional fields had real data.
  // We use this instead of relying on RJSF's if/then evaluation, which breaks in the diff
  // view because all values are encoded as "before\u001Fafter" strings — array fields like
  // data_distribution become strings, so `contains` conditions never match.
  const activeFieldKeys = useMemo(() => {
    return new Set<string>([
      ...Object.keys(sanitizedFormDataBefore || {}),
      ...Object.keys(sanitizedFormDataAfter || {}),
    ]);
  }, [sanitizedFormDataBefore, sanitizedFormDataAfter]);

  // Prepares the schema for diff rendering:
  // 1. Flattens array fields to strings (since all diff values are encoded strings)
  // 2. Replaces allOf/anyOf/oneOf if/then conditionals with only the fields that have actual data,
  //    because if/then can't be evaluated against diff-encoded values
  const transformSchemaForDiff = (schema: any, topLevel = false): any => {
    if (!schema || typeof schema !== 'object') return schema;
    const result = { ...schema };
    if (result.type === 'array') {
      result.type = 'string';
      delete result.items;
    }
    if (result.properties) {
      result.properties = Object.fromEntries(
        Object.entries(result.properties).map(([k, v]) => [k, transformSchemaForDiff(v)]),
      );
    }
    if (result.items) {
      result.items = transformSchemaForDiff(result.items);
    }
    if (topLevel) {
      for (const combiner of ['allOf', 'anyOf', 'oneOf'] as const) {
        if (Array.isArray(result[combiner])) {
          for (const subSchema of result[combiner]) {
            // Promote direct properties from non-conditional sub-schemas
            if (subSchema.properties && !subSchema.if) {
              result.properties = result.properties || {};
              for (const [key, fieldSchema] of Object.entries(subSchema.properties)) {
                result.properties[key] = transformSchemaForDiff(fieldSchema as any);
              }
            }
            // Promote conditional properties that have actual data
            for (const branch of ['then', 'else'] as const) {
              if (subSchema[branch]?.properties) {
                for (const [key, fieldSchema] of Object.entries(subSchema[branch].properties)) {
                  if (activeFieldKeys.has(key)) {
                    result.properties = result.properties || {};
                    result.properties[key] = transformSchemaForDiff(fieldSchema as any);
                  }
                }
              }
            }
          }
          delete result[combiner];
        }
      }
    }
    return result;
  };

  const diffSchema = useMemo(() => transformSchemaForDiff(filteredSchema, true), [filteredSchema, activeFieldKeys]);

  let widgetsOverrides: RegistryWidgetsType = {
    CheckboxWidget: (props: WidgetProps) => <DiffWidget {...props} />,
    CheckboxesWidget: (props: WidgetProps) => <DiffWidget {...props} />,
    TextWidget: (widgetProps: WidgetProps) => <DiffWidget {...widgetProps} />,
    SelectWidget: (widgetProps: WidgetProps) => <DiffWidget {...widgetProps} />,
    RadioWidget: (widgetProps: WidgetProps) => <DiffWidget {...widgetProps} />,
    GroupedMultiselectWidget: (widgetProps: WidgetProps) => <DiffWidget {...widgetProps} />,
  };
  const fieldsOverrides = useMemo(
    () => ({
      HierarchicalCheckboxField: (fieldProps: FieldProps) => (
        <DiffWidget {...(fieldProps as unknown as WidgetProps)} value={fieldProps.formData} />
      ),
    }),
    [],
  );
  let templates = { ArrayFieldTemplate: ReadOnlyArrayFieldTemplate, FieldTemplate: CompactFieldTemplate };
  const widgets: RegistryWidgetsType = useMemo(() => widgetsOverrides, [props.omniContext]);

  const getRootLevelErrorText = () => {
    const rootLevelErrors = props.errors.filter((error) => error.schemaPath == '#');
    if (rootLevelErrors.length == 0) {
      return;
    }
    return rootLevelErrors[0].message;
  };

  let uiSchema = {
    'ui:widget': 'checkbox',
    'ui:submitButtonOptions': {
      norender: true,
    },
    // Merge in uiSchema from JSON if provided
    ...(uiSchemaFromJson || {}),
  };
  if (filteredOrder?.length > 0) {
    uiSchema['ui:order'] = filteredOrder;
  }

  const valueToString = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'boolean') return String(val);
    if (typeof val === 'number') return String(val);
    if (typeof val === 'string') return val;
    if (Array.isArray(val)) {
      if (val.length > 0 && val[0]?.id !== undefined && val[0]?.label !== undefined) {
        return flattenTreeToText(val).join(', ');
      }
      return [...val].sort().join(', ');
    }
    return '';
  };

  const isPlainObject = (val: any): boolean => val !== null && typeof val === 'object' && !Array.isArray(val);

  const mergeObjects = (before: object, after: object) => {
    const result = {};
    const allKeys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);

    for (const key of allKeys) {
      const beforeVal = before?.[key];
      const afterVal = after?.[key];

      if (isPlainObject(beforeVal) || isPlainObject(afterVal)) {
        result[key] = mergeObjects(beforeVal || {}, afterVal || {});
      } else {
        const beforeStr = valueToString(beforeVal);
        const afterStr = valueToString(afterVal);
        result[key] =
          props.showChangesOnly && beforeStr === afterStr ? HIDDEN : `${beforeStr}${UNIT_SEPARATOR}${afterStr}`;
      }
    }
    return result;
  };

  return (
    <Form
      readonly={true}
      validator={validator}
      schema={diffSchema}
      formData={mergeObjects(sanitizedFormDataBefore, sanitizedFormDataAfter)}
      onChange={() => {}}
      transformErrors={() => {
        return props.errors;
      }}
      extraErrorsBlockSubmit={true}
      showErrorList={'bottom'}
      errorText={getRootLevelErrorText()}
      uiSchema={uiSchema}
      noHtml5Validate={true}
      widgets={widgets}
      fields={fieldsOverrides}
      omitExtraData={true}
      liveOmit={true}
      templates={templates}
    />
  );
};
